/**
 * TS mirror of infrastructure/lambdaFunctions/sharedModules/python/pricing_tiers.py.
 *
 * Used only to render the "Based on your volume, you qualify for…" preview
 * in the intake forms. The CardConnect submission is built backend-side
 * from the canonical Python tier table; this mirror is for UX only.
 *
 * IMPORTANT: When Michael's real tier values land, update BOTH this file
 * AND pricing_tiers.py together. The structural test in tests/
 * test_pricing_tiers.py asserts is_placeholder=True on every tier — once
 * that flips on the Python side, this file's placeholder values become
 * the user-visible source of truth for the preview, so they need to
 * match the real numbers.
 */

export type PciProgram = "CONCIERGE" | "ANNUAL";

/**
 * Fleet-card pricing (Voyager, WEX, PIN debit, EBT). Lives on the tier but
 * OUTSIDE `fees`: the CoPilot boarding API does not accept these fields, so
 * this is our display + MAR schedule, never part of the CardConnect payload.
 * Percentage fields are PERCENT NUMBERS (3.65 means 3.65%), display-only and
 * never converted for CardConnect, NOT fractions; per-txn fees are dollars.
 * ebtFnsNumber's default ("") is a display placeholder only -- it's a
 * merchant-specific USDA identifier, like an SSN, with no real tier-level
 * default; the admin always enters it per merchant as a 7-digit STRING whose
 * leading/trailing zeros are significant ("0712345"), never a number (issue
 * #197).
 *
 * Voyager and WEX used to be a single combined schedule
 * (wexVoyagerDiscountPct/wexVoyagerTransactionFee); they're now separate,
 * each with their own adjustable fields (see FLEET_OVERRIDE_FIELDS below).
 */
export interface FleetPricing {
  voyagerAuthFee: number | null;
  voyagerSalesDiscountPct: number | null;
  wexSalesDiscountPct: number | null;
  wexTransactionFee: number | null;
  wexChargebackFee: number | null;
  wexRetrievalFee: number | null;
  /** A PERCENT (0.15 means 0.15%), not a dollar amount -- see the note above. */
  pinDebitDiscountFee: number | null;
  /** null = mirror the credit schedule (PP is usually $0.02-0.05 higher on fee). */
  pinDebitTransactionFee: number | null;
  /** The merchant's USDA Food and Nutrition Service number -- an identifier,
   *  not a fee. A 7-digit STRING with significant leading/trailing zeros; no
   *  tier-level default exists for this (issue #197). */
  ebtFnsNumber: string | null;
  ebtTransactionFee: number | null;
}

/**
 * Clover Security + TransArmor pricing. Lives on the tier but OUTSIDE both
 * `fees` and `fleetPricing` -- a THIRD parallel object. Unlike fleetPricing
 * (never sent to CardConnect), this DOES get sent: CardConnect's merchant
 * boarding payload accepts a `cloverSecurityAndTransarmor` object alongside
 * `fees`/`pricing.icPlusPricing`. Same volume-independent-schedule treatment
 * as fleetPricing: no indication these vary by tier.
 *
 * transarmorDataProtectionFlg/transarmorTokenTypeCd/cloverSecurityBundleCd
 * stay null/false by default -- these are the two ENABLING fields (Data
 * Protection, Clover Security bundle); CardConnect rejects a fee for a
 * disabled product, so the whole block is omitted unless an operator
 * override actually turns a product on. Every other field has an
 * established $0.00 default, used only if a product IS later enabled
 * without its own override for that field.
 */
export interface CloverSecurityAndTransarmor {
  transarmorDataProtectionFlg: boolean | null;
  transarmorTokenTypeCd: string | null;
  transarmorMultiPayToken: string | null;
  transarmorMonthlyFee: number | null;
  cloverSecurityBundleCd: string | null;
  cloverSecurityPlusFee: number | null;
  pciComplianceServiceFee: number | null;
  pciComplianceServiceFeeFrequency: number | null;
  pciComplianceServiceFeeMonthToBill: string | null;
}

export interface PricingTier {
  tierId: string;
  label: string;
  minVolume: number;
  /** Exclusive upper bound; null means "no upper bound" for the top tier. */
  maxVolume: number | null;
  isPlaceholder: boolean;
  fees: {
    achBatchFee: number;
    addressVerifFee: number;
    annualMembershipFee: number;
    appFee: number;
    authFee: number;
    chargebackFee: number;
    ddaRejectFee: number;
    earlyCancelFee: number;
    minProcessFee: number;
    monthlyEquipmentRentalFee: number;
    /** Clover fees, in the fees object per CardConnect. Default 0; injected
     *  onto every tier (see PRICING_TIERS map). cloverPlatformFee is required
     *  by CardConnect when the application includes a Clover order. */
    cloverPlatformFee: number;
    cloverSecurityFee: number;
    /** STRING in CardConnect's docs — preserve quoting on the wire. */
    pciAnnualFee: string;
    pciConciergeMonthlyFee: number;
    pciNonComplianceFee: number;
    pciProgramCd: PciProgram;
    regProdMonthlyFee: number;
    retrievalFee: number;
    statementFee: number;
    transactionFee: number;
    voiceAuthFee: number;
    wirelessActivationFee: number;
    wirelessFee: number;
    duesAndAssessmentsFlg: boolean;
    passthruInterchgCostsFlg: boolean;
  };
  fleetPricing: FleetPricing;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmor;
  /**
   * Annual security bundle fee ($). CardConnect's CoPilot site has this input
   * but the boarding API has NO equivalent field, so -- like fleetPricing --
   * it's display-only and NEVER part of the CardConnect payload. It's a single
   * scalar (not a nested block) and the same $199 for every tier (Michael).
   * The operator can override it per application; the override is persisted to
   * our database only (row attr AnnualSecurityBundleFee).
   */
  annualSecurityBundleFee: number;
}

/**
 * Default Annual security bundle fee, applied to every tier. Mirrors
 * ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT in pricing_tiers.py -- edit both together.
 */
export const ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT = 199;

/**
 * Fleet pricing is the SAME regardless of volume (Michael, issue #119), so the
 * same defaults attach to every tier. Mirrors FLEET_PRICING_DEFAULTS in
 * pricing_tiers.py. Edit both together.
 */
export const FLEET_PRICING_DEFAULTS: FleetPricing = {
  voyagerAuthFee: 0.12,
  voyagerSalesDiscountPct: 3.65,
  wexSalesDiscountPct: 3.65,
  wexTransactionFee: 0.12,
  wexChargebackFee: 25.0,
  wexRetrievalFee: 15.0,
  pinDebitDiscountFee: 0.15,
  pinDebitTransactionFee: 0.1,
  ebtFnsNumber: "",
  ebtTransactionFee: 0.1,
};

/**
 * Clover Security + TransArmor is the SAME regardless of volume, same
 * treatment as fleet pricing -- one shared default block attaches to every
 * tier. Mirrors CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS in pricing_tiers.py.
 * Edit both together.
 *
 * DEFAULT IS FULLY OFF: neither TransArmor Data Protection nor a Clover
 * Security bundle. CardConnect rejects any fee for a disabled product, so the
 * backend omits the whole cloverSecurityAndTransarmor object unless an
 * operator override actually enables a product -- transarmorDataProtectionFlg/
 * cloverSecurityBundleCd/transarmorTokenTypeCd stay null/false to preserve
 * that gate. The other fields have an established $0.00 default.
 */
export const CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS: CloverSecurityAndTransarmor =
  {
    transarmorDataProtectionFlg: false,
    transarmorTokenTypeCd: null,
    transarmorMultiPayToken: "0.00",
    transarmorMonthlyFee: 0.0,
    cloverSecurityBundleCd: null,
    cloverSecurityPlusFee: 0.0,
    pciComplianceServiceFee: 0.0,
    pciComplianceServiceFeeFrequency: 0,
    pciComplianceServiceFeeMonthToBill: "0.00",
  };

// Seed tiers omit the derived blocks (fleetPricing, cloverSecurityAndTransarmor)
// and the injected Clover fees (cloverPlatformFee/cloverSecurityFee, added to
// every tier's fees in the PRICING_TIERS map below), so the tier literals stay
// focused on the volume-specific fee schedule.
type TierSeed = Omit<
  PricingTier,
  | "fleetPricing"
  | "cloverSecurityAndTransarmor"
  | "annualSecurityBundleFee"
  | "fees"
> & {
  fees: Omit<PricingTier["fees"], "cloverPlatformFee" | "cloverSecurityFee">;
};

const TIERS_WITHOUT_FLEET: TierSeed[] = [
  {
    tierId: "tier1",
    label: "Tier 1 — under $200K/mo",
    minVolume: 0,
    maxVolume: 200_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier2",
    label: "Tier 2 — $200K to $300K/mo",
    minVolume: 200_000,
    maxVolume: 300_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier3",
    label: "Tier 3 — $300K to $400K/mo",
    minVolume: 300_000,
    maxVolume: 400_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier4",
    label: "Tier 4 — $400K to $500K/mo",
    minVolume: 400_000,
    maxVolume: 500_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier5",
    label: "Tier 5 — $500K to $600K/mo",
    minVolume: 500_000,
    maxVolume: 600_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier6",
    label: "Tier 6 — $600K to $700K/mo",
    minVolume: 600_000,
    maxVolume: 700_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier7",
    label: "Tier 7 — $700K to $1M/mo",
    minVolume: 700_000,
    maxVolume: 1_000_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier8",
    label: "Tier 8 — $1M to $1.5M/mo",
    minVolume: 1_000_000,
    maxVolume: 1_500_000,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
  {
    tierId: "tier9",
    label: "Tier 9 — $1.5M+ mo",
    minVolume: 1_500_000,
    maxVolume: null,
    isPlaceholder: false,
    fees: {
      achBatchFee: 0.1,
      addressVerifFee: 0.05,
      annualMembershipFee: 0.0,
      appFee: 0.0,
      authFee: 0.1,
      chargebackFee: 25.0,
      ddaRejectFee: 25.0,
      earlyCancelFee: 750.0,
      minProcessFee: 25.0,
      monthlyEquipmentRentalFee: 0.0,
      pciAnnualFee: "199.00",
      pciConciergeMonthlyFee: 32.99,
      pciNonComplianceFee: 29.95,
      pciProgramCd: "ANNUAL",
      regProdMonthlyFee: 0.0,
      retrievalFee: 15.0,
      statementFee: 0.0,
      transactionFee: 0.0,
      voiceAuthFee: 1.95,
      wirelessActivationFee: 0.0,
      wirelessFee: 0.0,
      duesAndAssessmentsFlg: true,
      passthruInterchgCostsFlg: true,
    },
  },
];

// Attach the (volume-independent) fleet + Clover Security/TransArmor blocks
// to every tier. Fresh object per tier so an in-place edit can't bleed
// across tiers.
export const PRICING_TIERS: PricingTier[] = TIERS_WITHOUT_FLEET.map((t) => ({
  ...t,
  // Clover fees live in the fees object per CardConnect; default 0 on every
  // tier, operator-overridable via the fee-override path.
  fees: { ...t.fees, cloverPlatformFee: 0, cloverSecurityFee: 0 },
  fleetPricing: { ...FLEET_PRICING_DEFAULTS },
  cloverSecurityAndTransarmor: { ...CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS },
  // Volume-independent, same as the two blocks above (see the field's doc on
  // PricingTier). Single scalar, never a CardConnect field.
  annualSecurityBundleFee: ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT,
}));

/**
 * Return the tier matching `monthlyVolume`, or null if the input is missing
 * or unparseable.
 *
 * Selection rules mirror the Python helper:
 *   - null / non-numeric / NaN -> null (no preview rendered)
 *   - <= 0 -> first (lowest) tier
 *   - inside a band [min, max) -> that tier
 *   - above the top band's min -> top tier (maxVolume=null)
 */
export function selectPricingTier(
  monthlyVolume: number | string | null | undefined,
): PricingTier | null {
  if (
    monthlyVolume === null ||
    monthlyVolume === undefined ||
    monthlyVolume === ""
  ) {
    return null;
  }
  const volume =
    typeof monthlyVolume === "number" ? monthlyVolume : Number(monthlyVolume);
  if (!Number.isFinite(volume)) return null;
  if (volume <= 0) return PRICING_TIERS[0];
  for (const tier of PRICING_TIERS) {
    if (tier.maxVolume === null || volume < tier.maxVolume) return tier;
  }
  return PRICING_TIERS[PRICING_TIERS.length - 1];
}

/**
 * State-specific Early Termination Fee caps. Mirrors STATE_ETF_CAPS in
 * pricing_tiers.py (the authoritative boarding-blocker) -- surfaced here so the
 * operator sees the error before submit rather than as a CardConnect 409 (code
 * 9872, "The Early Termination Fee cannot be greater than $500.00"). Dollars,
 * matching earlyCancelFee. Table-driven: adding a state is a one-line change.
 * Keep both tables in step.
 */
export const STATE_ETF_CAPS: Record<string, number> = { MD: 500 };

/**
 * Return an ETF-cap error message when `stateCd` caps the ETF and the effective
 * earlyCancelFee exceeds that cap, else null. Mirrors pricing_tiers.etf_cap_error:
 * a blank/uncapped state, a non-numeric fee, or a fee within the cap returns
 * null so callers can invoke it unconditionally.
 */
export function etfCapError(
  stateCd: string | null | undefined,
  effectiveEarlyCancelFee: number | null | undefined,
): string | null {
  if (!stateCd) return null;
  const state = stateCd.trim().toUpperCase();
  const cap = STATE_ETF_CAPS[state];
  if (cap === undefined) return null;
  if (
    effectiveEarlyCancelFee === null ||
    effectiveEarlyCancelFee === undefined ||
    !Number.isFinite(effectiveEarlyCancelFee)
  )
    return null;
  if (effectiveEarlyCancelFee > cap)
    return `Early Termination Fee cannot exceed $${cap.toLocaleString()} in ${state}.`;
  return null;
}

/** USD currency formatter (no fractional cents unless the value has them). */
const _currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/** Render a tier-table value (string for pciAnnualFee, number otherwise). */
export function formatFee(value: number | string): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return _currency.format(n);
}

/**
 * Resolve the effective Annual security bundle fee for a display surface: the
 * operator override when it's a usable non-negative number, else the tier
 * default, else ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT (so a caller with no tier
 * still shows $199 rather than nothing). `overrideRaw` is the raw form string
 * (may carry a "$" the operator typed) or a number from the wire.
 */
export function effectiveAnnualSecurityBundleFee(
  tierValue: number | null | undefined,
  overrideRaw: string | number | null | undefined,
): number {
  const rawText = String(overrideRaw ?? "").trim();
  // Drop a "$"/","/whitespace the operator may have typed (the input allows a
  // "$" for familiarity) -- matches lib/formatters.stripAmountFormatting without
  // pulling a dependency into this otherwise standalone tier mirror.
  const cleaned =
    typeof overrideRaw === "number"
      ? overrideRaw
      : Number(rawText.replace(/[$,\s]/g, ""));
  if (Number.isFinite(cleaned) && cleaned >= 0 && rawText !== "") return cleaned;
  if (typeof tierValue === "number" && Number.isFinite(tierValue))
    return tierValue;
  return ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT;
}

/** Render a percent-kind fleet override's tier-default value ("3.65" -> "3.65%"). */
export function formatPercent(value: number | string): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${n}%`;
}

/** An EBT FNS number is exactly 7 ASCII digits (leading/trailing zeros
 *  significant). */
export const FNS_NUMBER_DIGITS = 7;

/** True when `value` is exactly 7 digits -- the FNS number format (#197). */
export function isValidFnsNumber(value: string): boolean {
  return new RegExp(`^\\d{${FNS_NUMBER_DIGITS}}$`).test(value);
}

/**
 * Render an ebtFnsNumber for display. The FNS number is a STRING whose
 * leading/trailing zeros are significant (issue #197), so it is shown exactly
 * as entered -- NEVER numerically truncated or reparsed. The empty-string
 * placeholder (see FLEET_PRICING_DEFAULTS) renders as a zero-filled 7-digit
 * stand-in so it doesn't read as a stray blank; any other value is returned
 * verbatim (trimmed) so "0712345" survives intact.
 */
export function formatFnsNumber(value: number | string | null): string {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (text === "" || text === "0") return "".padStart(FNS_NUMBER_DIGITS, "0");
  return text;
}

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * Canonical full month name for a pciComplianceServiceFeeMonthToBill value, or
 * null when it isn't a recognizable month. The field is a dropdown of the 12
 * month names now, but this still normalizes a legacy stored value -- a full
 * name in any case ("march", "MARCH") or a 1-12 index (the CardConnect wire
 * form); an abbreviation ("Mar"), an out-of-range number, or free text is
 * rejected. Blank returns null too -- callers treat blank as "no month".
 */
export function canonicalMonthToBill(value: number | string): string | null {
  const text = String(value).trim();
  if (!text) return null;
  const n = Number(text);
  if (Number.isInteger(n) && n >= 1 && n <= 12) return MONTH_NAMES[n - 1];
  const lower = text.toLowerCase();
  return MONTH_NAMES.find((m) => m.toLowerCase() === lower) ?? null;
}

/**
 * Single source of truth for the "Manual fee adjustment" admin section
 * (NewApplication's `form` + `sendIntakeForm`): every field on
 * PricingTier["fees"], in display order, with the input widget it needs.
 * Adding a fee to a tier's `fees` object should mean adding one row here.
 */
export type FeeOverrideFieldKind =
  | "currency" // plain number, submitted as a number
  | "currencyString" // number-shaped but submitted as a string (pciAnnualFee)
  | "pciProgram" // CONCIERGE | ANNUAL select
  | "flag"; // boolean select (tri-state: no override / true / false)

export interface FeeOverrideField {
  key: keyof PricingTier["fees"];
  label: string;
  kind: FeeOverrideFieldKind;
}

/**
 * Fee-override keys that CardConnect groups under Clover. They live in the
 * `fees` object like every other fee (so they use the fee-override path), but
 * the UI renders their inputs inside the Clover Security section rather than
 * the general Manual fee adjustment section. Both places filter on this set.
 */
export const CLOVER_FEE_OVERRIDE_KEYS = [
  "cloverPlatformFee",
  "cloverSecurityFee",
] as const;

export const FEE_OVERRIDE_FIELDS: FeeOverrideField[] = [
  { key: "authFee", label: "Authorization fee", kind: "currency" },
  { key: "transactionFee", label: "Transaction fee", kind: "currency" },
  { key: "minProcessFee", label: "Minimum processing fee", kind: "currency" },
  { key: "cloverPlatformFee", label: "Clover platform fee", kind: "currency" },
  { key: "cloverSecurityFee", label: "Clover security fee", kind: "currency" },
  { key: "achBatchFee", label: "ACH batch fee", kind: "currency" },
  {
    key: "addressVerifFee",
    label: "Address verification (AVS) fee",
    kind: "currency",
  },
  {
    key: "annualMembershipFee",
    label: "Annual membership fee",
    kind: "currency",
  },
  { key: "appFee", label: "Application fee", kind: "currency" },
  { key: "chargebackFee", label: "Chargeback fee", kind: "currency" },
  { key: "ddaRejectFee", label: "DDA reject fee", kind: "currency" },
  { key: "earlyCancelFee", label: "Early cancellation fee", kind: "currency" },
  {
    key: "monthlyEquipmentRentalFee",
    label: "Monthly equipment rental fee",
    kind: "currency",
  },
  {
    key: "regProdMonthlyFee",
    label: "Regulatory product monthly fee",
    kind: "currency",
  },
  { key: "retrievalFee", label: "Retrieval fee", kind: "currency" },
  { key: "statementFee", label: "Statement fee", kind: "currency" },
  { key: "voiceAuthFee", label: "Voice auth fee", kind: "currency" },
  {
    key: "wirelessActivationFee",
    label: "Wireless activation fee",
    kind: "currency",
  },
  { key: "wirelessFee", label: "Wireless fee", kind: "currency" },
  { key: "pciAnnualFee", label: "PCI annual fee", kind: "currencyString" },
  {
    key: "pciConciergeMonthlyFee",
    label: "PCI concierge monthly fee",
    kind: "currency",
  },
  {
    key: "pciNonComplianceFee",
    label: "PCI non-compliance fee",
    kind: "currency",
  },
  { key: "pciProgramCd", label: "PCI program", kind: "pciProgram" },
  {
    key: "duesAndAssessmentsFlg",
    label: "Dues & assessments pass-through",
    kind: "flag",
  },
  {
    key: "passthruInterchgCostsFlg",
    label: "Interchange cost pass-through",
    kind: "flag",
  },
];

/**
 * Single source of truth for the "Fleet pricing adjustment" admin section
 * (NewApplication's `form` + `sendIntakeForm`): the fleet fields marked
 * admin-adjustable per application. Deliberately a SEPARATE parallel object to
 * FEE_OVERRIDE_FIELDS: fleet overrides are validated + persisted for future
 * MAR/display use only and MUST NEVER reach the CardConnect payload (the
 * CoPilot API rejects Voyager/WEX/EBT/PIN-debit fields). The floor fields
 * remain intentionally excluded (fixed guardrail, unrelated to this list).
 *
 * `kind` drives both display formatting and validation range:
 *   - "currency": a dollar fee. Formatted with formatFee, validated against
 *     FEE_OVERRIDE_MAX (see NewApplication.tsx).
 *   - "percent": a percent number (3.65 means 3.65%). Formatted as "N%".
 *     Validated the same as "currency" -- there's no tighter 0-100 range
 *     enforced today, matching how these fields were already validated
 *     before the kind split (see NewApplication.tsx's
 *     fleetOverridesInvalidFields).
 *   - "number": a plain identifier, not a fee (only ebtFnsNumber today).
 *     No currency formatting, no $ range cap, must be a whole number.
 */
export type FleetOverrideFieldKind = "currency" | "percent" | "number";

export interface FleetOverrideField {
  key: keyof FleetPricing;
  label: string;
  kind: FleetOverrideFieldKind;
}

export const FLEET_OVERRIDE_FIELDS: FleetOverrideField[] = [
  {
    key: "voyagerAuthFee",
    label: "Voyager authorization fee",
    kind: "currency",
  },
  {
    key: "voyagerSalesDiscountPct",
    label: "Voyager sales discount",
    kind: "percent",
  },
  { key: "wexSalesDiscountPct", label: "Wex sales discount", kind: "percent" },
  { key: "wexTransactionFee", label: "Wex transaction fee", kind: "currency" },
  { key: "wexChargebackFee", label: "Wex chargeback fee", kind: "currency" },
  { key: "wexRetrievalFee", label: "Wex retrieval fee", kind: "currency" },
  {
    key: "pinDebitDiscountFee",
    label: "PIN debit discount fee",
    kind: "percent",
  },
  {
    key: "pinDebitTransactionFee",
    label: "PIN debit transaction fee",
    kind: "currency",
  },
  { key: "ebtFnsNumber", label: "EBT FNS #", kind: "number" },
  { key: "ebtTransactionFee", label: "EBT transaction fee", kind: "currency" },
];

/**
 * Field-key groupings for FLEET_OVERRIDE_FIELDS, mirroring how CardConnect's
 * own MPA/CoPilot lays out fleet-adjacent pricing as separate sections
 * rather than one flat list (Michael, pricingSummarySeparation). Shared
 * source of truth for both the admin FleetPricingAdjustmentSection
 * (NewApplication.tsx) and the merchant-facing pricing summary
 * (IntakeForm.tsx) so the two stay grouped identically.
 */
export const VOYAGER_KEYS: (keyof FleetPricing)[] = [
  "voyagerAuthFee",
  "voyagerSalesDiscountPct",
];
export const WEX_KEYS: (keyof FleetPricing)[] = [
  "wexSalesDiscountPct",
  "wexTransactionFee",
  "wexChargebackFee",
  "wexRetrievalFee",
];
export const PIN_DEBIT_KEYS: (keyof FleetPricing)[] = [
  "pinDebitDiscountFee",
  "pinDebitTransactionFee",
];
export const EBT_KEYS: (keyof FleetPricing)[] = [
  "ebtFnsNumber",
  "ebtTransactionFee",
];

/**
 * Single source of truth for the "Clover Security + TransArmor" admin
 * section (NewApplication's `form` + `sendIntakeForm`): every field on the
 * tier's cloverSecurityAndTransarmor block. A THIRD parallel object to
 * FEE_OVERRIDE_FIELDS/FLEET_OVERRIDE_FIELDS -- unlike fleet overrides, this
 * DOES reach the CardConnect payload (merchant.cloverSecurityAndTransarmor).
 *
 * `kind` drives both display formatting and validation:
 *   - "currency": a dollar fee. Formatted with formatFee, validated against
 *     FEE_OVERRIDE_MAX (see NewApplication.tsx).
 *   - "number": a plain number, not a fee (pciComplianceServiceFeeFrequency
 *     -- a frequency code, not a dollar amount). No currency formatting.
 *   - "flag": tri-state boolean override (no override / on / off), same
 *     pattern as duesAndAssessmentsFlg in FEE_OVERRIDE_FIELDS.
 *   - "string": free text, no numeric validation beyond a length cap --
 *     CardConnect's own docs don't document an enum for these.
 *   - "select": a dropdown constrained to `options`.
 */
export type CloverSecurityAndTransarmorFieldKind =
  | "currency"
  | "number"
  | "flag"
  | "string"
  | "select";

export interface CloverSecurityAndTransarmorField {
  key: keyof CloverSecurityAndTransarmor;
  label: string;
  kind: CloverSecurityAndTransarmorFieldKind;
  /** For kind: "select" -- the allowed option values. Rendered as a
   *  dropdown with a leading empty option (see `emptyLabel`). */
  options?: readonly string[];
  /** For kind: "select" -- label for the leading empty option. Defaults to a
   *  "(Tier Default)" line built from the tier value; set this for a field
   *  whose unset state isn't a tier default (e.g. the month-to-bill picker,
   *  which defaults to no month selected). */
  emptyLabel?: string;
}

/**
 * CardConnect Clover Security bundle codes (cloverSecurityBundleCd). Source:
 * the CoPilot template dropdown (Susan, 2026-08-03). Authoritative list is
 * CardConnect's /lookup/cloverSecurityBundleCodes endpoint.
 */
export const CLOVER_SECURITY_BUNDLE_CODES = [
  "CSPFB",
  "PCI",
  "TDPPCI",
  "TDP",
] as const;

/**
 * CardConnect TransArmor token type codes (transarmorTokenTypeCd). SINGLE was
 * confirmed accepted by CardConnect; MULTI is the other documented value.
 * Authoritative list is CardConnect's /lookup/transarmorTokenTypeCodes.
 */
export const TRANSARMOR_TOKEN_TYPE_CODES = ["SINGLE", "MULTI"] as const;

export const CLOVER_SECURITY_AND_TRANSARMOR_FIELDS: CloverSecurityAndTransarmorField[] =
  [
    {
      key: "transarmorDataProtectionFlg",
      label: "TransArmor data protection",
      kind: "flag",
    },
    {
      key: "transarmorTokenTypeCd",
      label: "TransArmor token type code",
      kind: "select",
      options: TRANSARMOR_TOKEN_TYPE_CODES,
    },
    {
      key: "transarmorMultiPayToken",
      label: "TransArmor multi-pay token",
      kind: "string",
    },
    {
      key: "transarmorMonthlyFee",
      label: "TransArmor monthly fee",
      kind: "currency",
    },
    {
      key: "cloverSecurityBundleCd",
      label: "Clover security bundle code",
      kind: "select",
      options: CLOVER_SECURITY_BUNDLE_CODES,
    },
    {
      key: "cloverSecurityPlusFee",
      label: "Clover security plus fee",
      kind: "currency",
    },
    {
      key: "pciComplianceServiceFee",
      label: "PCI compliance service fee",
      kind: "currency",
    },
    {
      key: "pciComplianceServiceFeeFrequency",
      label: "PCI compliance service fee frequency",
      kind: "number",
    },
    {
      key: "pciComplianceServiceFeeMonthToBill",
      label: "PCI compliance service fee month to bill",
      kind: "select",
      options: MONTH_NAMES,
      emptyLabel: "No month selected",
    },
  ];
